/**
 * Firebase configuration and services
 */

import { initializeApp, getApps } from 'firebase/app';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
  signInWithCustomToken,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  updatePassword,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  type Auth,
  type OAuthCredential,
  User
} from 'firebase/auth';
import { learnFirebaseConfig } from './learnFirebase';
import { startAuth } from './startAuth';

/**
 * The studio project's web config, as a compile-time default.
 *
 * Same reasoning as LEARN_FIREBASE_DEFAULTS in learnFirebase.ts, and the same
 * incident: a build that never received VITE_FIREBASE_* still compiles, still
 * deploys and still renders — it just cannot sign anyone in. That exact bundle
 * reached BOTH app.4klabs.ai and learn.4klabs.ai and took login down on both.
 *
 * A Firebase web config is public by design: it identifies the project and
 * authorises nothing, access being governed by security rules and the
 * authorised-domains list. These values already ship inside every bundle we
 * serve and sit in scripts/aws-infra/deploy-frontend.sh in this repo. Env still
 * wins when set, so pointing a build at another project needs no edit here.
 */
const FIREBASE_DEFAULTS = {
  apiKey: 'AIzaSyDgTNFTVaRbPoZbO3XOUGkfMo9Y66XA7VA',
  authDomain: 'rahul-project-468115.firebaseapp.com',
  projectId: 'rahul-project-468115',
  storageBucket: 'rahul-project-468115.firebasestorage.app',
  messagingSenderId: '912979888493',
  appId: '1:912979888493:web:e5e69e5e05a45fcd26cc63',
} as const;

// Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || FIREBASE_DEFAULTS.apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || FIREBASE_DEFAULTS.authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || FIREBASE_DEFAULTS.projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || FIREBASE_DEFAULTS.storageBucket,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || FIREBASE_DEFAULTS.messagingSenderId,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || FIREBASE_DEFAULTS.appId
};

export type AuthProject = 'studio' | 'fourklabs';

/**
 * Sign-in is moving from the studio project to `fourklabs-learn` one account at
 * a time. The browser remembers which project it is signed into here; `?auth=`
 * overrides it for testing. The fourklabs app is a separate named app, never the
 * learner portal's `learn` app, so learner sessions are untouched.
 */
const AUTH_PROJECT_KEY = 'ivy.authProject';

function readActiveProject(): AuthProject {
  try {
    const choice = new URLSearchParams(window.location.search).get('auth');
    if (choice === 'fourklabs' || choice === 'studio') localStorage.setItem(AUTH_PROJECT_KEY, choice);
    return localStorage.getItem(AUTH_PROJECT_KEY) === 'fourklabs' ? 'fourklabs' : 'studio';
  } catch {
    return 'studio';
  }
}

/**
 * The fourklabs app signs in on the app's own host, set per environment at build time
 * (scripts/aws-infra/frontend-env.sh): CloudFront routes <host>/__/auth/* to fourklabs-learn. Never
 * derived from location: a host claimed by another project's Hosting is refused
 * (auth/invalid-continue-uri, 18 Oct 2026), and scripts/auth-migration/authuri-matrix.mjs checks
 * each built value. Local dev falls back to learn.4klabs.ai.
 */
const fourklabsFirebaseConfig = () => ({
  ...learnFirebaseConfig,
  authDomain: import.meta.env.VITE_FOURKLABS_AUTH_DOMAIN || learnFirebaseConfig.authDomain,
});

// Log configuration status for debugging
console.log('Firebase Config Status:', {
  hasApiKey: !!firebaseConfig.apiKey,
  hasAuthDomain: !!firebaseConfig.authDomain,
  hasProjectId: !!firebaseConfig.projectId,
  environment: import.meta.env.MODE
});

// Initialize Firebase only if config is available
let app: any = null;
let auth: any;
let studioAuth: Auth | null = null;
let fourklabsAuth: Auth | null = null;
let activeProject: AuthProject = readActiveProject();

/** The Auth instance for a project. The fourklabs app starts only when first needed. */
export function authFor(project: AuthProject): Auth {
  if (project === 'studio') return studioAuth as Auth;
  if (!fourklabsAuth) {
    const existing = getApps().find((a) => a.name === 'fourklabs');
    fourklabsAuth = startAuth(existing ?? initializeApp(fourklabsFirebaseConfig(), 'fourklabs'));
  }
  return fourklabsAuth;
}

try {
  if (firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId) {
    app = initializeApp(firebaseConfig);
    studioAuth = startAuth(app);
    auth = authFor(activeProject);
    console.log('Firebase initialized successfully');
  } else {
    console.warn('Firebase config is incomplete. Running in mock mode.');
    // Create mock auth object for development without Firebase
    auth = {
      currentUser: null,
      onAuthStateChanged: (callback: any) => {
        // Immediately call with null user
        setTimeout(() => callback(null), 0);
        // Return unsubscribe function
        return () => {};
      }
    };
  }
} catch (error) {
  console.error('Firebase initialization error:', error);
  // Create mock auth object
  auth = {
    currentUser: null,
    onAuthStateChanged: (callback: any) => {
      setTimeout(() => callback(null), 0);
      return () => {};
    }
  };
}

export const getActiveAuthProject = (): AuthProject => activeProject;

export const otherAuthProject = (project: AuthProject): AuthProject =>
  project === 'studio' ? 'fourklabs' : 'studio';

/** Which project issued a Firebase email-action link, from the link's apiKey. */
export const authProjectForApiKey = (apiKey: string | null): AuthProject | null => {
  if (apiKey === firebaseConfig.apiKey) return 'studio';
  if (apiKey === learnFirebaseConfig.apiKey) return 'fourklabs';
  return null;
};

type AuthListener = { callback: (user: User | null) => void; unsubscribe: () => void };
const authListeners = new Set<AuthListener>();
let stopTokenSync: () => void = () => {};

/**
 * Makes a project the one every token getter and auth-state subscriber follows.
 * Subscribers are moved to the new project's user, so a switch never reports a
 * sign-out.
 */
export const activateAuthProject = (project: AuthProject): void => {
  if (!app || project === activeProject) return;
  activeProject = project;
  try { localStorage.setItem(AUTH_PROJECT_KEY, project); } catch { /* ignore */ }
  auth = authFor(project);
  for (const listener of authListeners) {
    listener.unsubscribe();
    listener.unsubscribe = onAuthStateChanged(auth, listener.callback);
  }
  syncBearerToken();
};

export const signOutOfAuthProject = async (project: AuthProject): Promise<void> => {
  if (!app) return;
  await signOut(authFor(project));
};

/** Silent handover: sign in on the target with a server-minted custom token, then leave the other project. */
export const switchAuthProject = async (target: AuthProject, customToken: string): Promise<void> => {
  const previous = activeProject;
  await signInWithCustomToken(authFor(target), customToken);
  activateAuthProject(target);
  if (previous !== target) {
    await signOutOfAuthProject(previous).catch((err) => console.warn('Sign-out of the previous project failed:', err));
  }
};

// Configure Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
// Force account selection on every sign-in to prevent confusion with multiple accounts
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

/**
 * Google popup on one project, without making it the active one.
 */
export const signInWithGoogleOn = async (
  project: AuthProject
): Promise<{ user: User; credential: OAuthCredential | null }> => {
  if (!app) {
    throw new Error('Firebase is not configured. Please set up Firebase environment variables.');
  }
  try {
    const result = await signInWithPopup(authFor(project), googleProvider);
    return { user: result.user, credential: GoogleAuthProvider.credentialFromResult(result) };
  } catch (error) {
    console.error('Google sign in failed:', error);
    throw error;
  }
};

/**
 * Starts a project's Auth and its Google sign-in frame now, so a later click opens the popup with no network
 * wait in between. The SDK already does this on Safari and phones, but only once the project's Auth exists,
 * and the fourklabs one starts on first use; on desktop Chrome it loads the frame inside the click.
 */
export const warmAuthProject = (project: AuthProject): void => {
  if (!app) return;
  const target = authFor(project) as Auth & { _popupRedirectResolver?: { _initialize?: (a: Auth) => Promise<unknown> } };
  void target
    .authStateReady()
    .then(() => target._popupRedirectResolver?._initialize?.(target))
    .catch(() => {});
};

/** Google sign-in in this tab: the page leaves for Google and comes back to this URL. */
export const startGoogleRedirect = async (project: AuthProject): Promise<void> => {
  if (!app) throw new Error('Firebase is not configured. Please set up Firebase environment variables.');
  await signInWithRedirect(authFor(project), googleProvider);
};

/** The Google sign-in this tab came back from, without making the project the active one; null if none. */
export const googleRedirectResult = async (
  project: AuthProject
): Promise<{ user: User; credential: OAuthCredential | null } | null> => {
  if (!app) return null;
  const result = await getRedirectResult(authFor(project));
  return result ? { user: result.user, credential: GoogleAuthProvider.credentialFromResult(result) } : null;
};

/**
 * Reuses the Google credential from one project's popup on the other, so a
 * fallback needs no second popup.
 */
export const signInWithGoogleCredential = async (project: AuthProject, credential: OAuthCredential): Promise<User> => {
  const result = await signInWithCredential(authFor(project), credential);
  return result.user;
};

/**
 * Sign in with Google popup
 */
export const signInWithGoogle = async (project: AuthProject = activeProject): Promise<User> => {
  const { user } = await signInWithGoogleOn(project);
  activateAuthProject(project);
  return user;
};

/**
 * Sign out user and clear all stored tokens
 */
export const signOutUser = async (): Promise<void> => {
  // Always clear localStorage tokens regardless of Firebase state
  localStorage.removeItem('bearer_token');
  localStorage.removeItem('auth_token');
  localStorage.removeItem('ivy_chat_session_token');

  if (!app) {
    console.warn('Firebase is not configured. Mock sign out.');
    return;
  }
  try {
    for (const instance of [studioAuth, fourklabsAuth]) {
      if (instance) await signOut(instance);
    }
    console.log('✅ User signed out and tokens cleared');
  } catch (error) {
    console.error('Sign out failed:', error);
    throw error;
  }
};

const AUTH_RESTORE_WAIT_MS = 10_000;

/**
 * Wait until Firebase has restored the signed-in user after a page load. Until then currentUser is null
 * even for a signed-in user, and a caller would fall back to a stale stored token (and a 401 would read as
 * signed out). Bounded, so a restore that never finishes can't hang a request.
 */
async function authRestored(): Promise<void> {
  if (!auth?.authStateReady) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    auth.authStateReady(),
    new Promise<void>((resolve) => { timer = setTimeout(resolve, AUTH_RESTORE_WAIT_MS); }),
  ]);
  clearTimeout(timer);
}

/**
 * Get current user's ID token
 */
export const getCurrentUserToken = async (): Promise<string | null> => {
  if (!auth) return null;
  await authRestored();
  if (!auth.currentUser) {
    return null;
  }
  
  try {
    return await auth.currentUser.getIdToken();
  } catch (error) {
    console.error('Failed to get user token:', error);
    return null;
  }
};

/**
 * Subscribe to auth state changes
 */
export const subscribeToAuthState = (callback: (user: User | null) => void) => {
  if (!auth || !auth.onAuthStateChanged) {
    console.warn('Auth not available, returning mock unsubscribe');
    // Call callback with null user
    setTimeout(() => callback(null), 0);
    return () => {};
  }
  const listener: AuthListener = { callback, unsubscribe: () => {} };
  authListeners.add(listener);
  listener.unsubscribe = onAuthStateChanged(auth, callback);
  return () => {
    listener.unsubscribe();
    authListeners.delete(listener);
  };
};

/**
 * Refresh user token
 */
export const refreshUserToken = async (): Promise<string | null> => {
  if (!auth) return null;
  await authRestored();
  if (!auth.currentUser) {
    return null;
  }
  
  try {
    return await auth.currentUser.getIdToken(true); // Force refresh
  } catch (error) {
    console.error('Failed to refresh user token:', error);
    return null;
  }
};

/**
 * Sign in with email and password
 */
export const signInWithEmailPassword = async (
  email: string,
  password: string,
  project: AuthProject = activeProject
): Promise<User> => {
  if (!app) {
    throw new Error('Firebase is not configured. Please set up Firebase environment variables.');
  }
  try {
    const result = await signInWithEmailAndPassword(authFor(project), email, password);
    activateAuthProject(project);
    return result.user;
  } catch (error: any) {
    console.error('Email/password sign in failed:', error);
    if (error.code === 'auth/wrong-password') {
      throw new Error('Incorrect password');
    } else if (error.code === 'auth/user-not-found') {
      throw new Error('No user found with this email');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    } else if (error.code === 'auth/user-disabled') {
      throw new Error('This account has been disabled');
    }
    throw error;
  }
};

/**
 * Sign up with email and password. Creates the Firebase account and immediately
 * sends a verification email (the app gates access on verification).
 */
export const signUpWithEmailPassword = async (
  email: string,
  password: string,
  project: AuthProject = activeProject
): Promise<User> => {
  if (!app) {
    throw new Error('Firebase is not configured. Please set up Firebase environment variables.');
  }
  try {
    const result = await createUserWithEmailAndPassword(authFor(project), email, password);
    activateAuthProject(project);
    try {
      await sendEmailVerification(result.user);
    } catch (verifyErr) {
      // Account exists; a failed verification send shouldn't block signup — the
      // user can resend from the verify screen.
      console.warn('Verification email send failed (can resend):', verifyErr);
    }
    return result.user;
  } catch (error: any) {
    console.error('Email/password sign up failed:', error);
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('An account with this email already exists. Try signing in instead.');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak. Use at least 6 characters.');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    }
    throw error;
  }
};

/**
 * Send a password-reset email (Firebase-hosted reset flow).
 */
export const sendPasswordReset = async (email: string, project: AuthProject = activeProject): Promise<void> => {
  if (!app) {
    throw new Error('Firebase is not configured. Please set up Firebase environment variables.');
  }
  try {
    await sendPasswordResetEmail(authFor(project), email);
  } catch (error: any) {
    console.error('Password reset failed:', error);
    if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    }
    // Don't reveal whether an account exists — treat user-not-found as success-ish.
    if (error.code === 'auth/user-not-found') {
      return;
    }
    throw error;
  }
};

/**
 * Resend the verification email to the currently signed-in user.
 */
export const resendVerificationEmail = async (): Promise<void> => {
  if (!auth || !auth.currentUser) {
    throw new Error('No user is currently signed in');
  }
  await sendEmailVerification(auth.currentUser);
};

/**
 * Reload the current user from Firebase and force a token refresh, so a
 * just-completed email verification is reflected in user.emailVerified AND in
 * the next ID token (the backend reads email_verified from the token).
 * Returns the up-to-date emailVerified flag.
 */
export const refreshEmailVerified = async (): Promise<boolean> => {
  if (!auth || !auth.currentUser) return false;
  try {
    await auth.currentUser.reload();
    await auth.currentUser.getIdToken(true); // force refresh → new token carries email_verified
    return !!auth.currentUser.emailVerified;
  } catch (error) {
    console.error('Failed to refresh email verification state:', error);
    return false;
  }
};

/**
 * Update user password
 */
export const updateUserPassword = async (newPassword: string): Promise<void> => {
  if (!auth || !auth.currentUser) {
    throw new Error('No user is currently signed in');
  }
  
  try {
    await updatePassword(auth.currentUser, newPassword);
  } catch (error: any) {
    console.error('Failed to update password:', error);
    if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak. Must be at least 6 characters');
    } else if (error.code === 'auth/requires-recent-login') {
      throw new Error('Please sign in again to change your password');
    }
    throw error;
  }
};

/**
 * Check if user must change password
 */
export const checkMustChangePassword = async (): Promise<boolean> => {
  try {
    const token = await getCurrentUserToken();
    if (!token) return false;
    
    const response = await fetch('/api/admin-console/must-change-password', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.mustChangePassword === true;
    }
    return false;
  } catch (error) {
    console.error('Failed to check password change requirement:', error);
    return false;
  }
};

// Use onIdTokenChanged to keep localStorage in sync with Firebase's automatic token refresh.
// Firebase SDK internally refreshes ID tokens ~5 min before the 60-min expiry.
// onIdTokenChanged fires on: login, logout, AND automatic token refresh — unlike
// onAuthStateChanged which only fires on login/logout.
function syncBearerToken() {
  stopTokenSync();
  stopTokenSync = onIdTokenChanged(auth, async (user: User | null) => {
    if (user) {
      try {
        const freshToken = await user.getIdToken();
        localStorage.setItem('bearer_token', freshToken);
      } catch (err) {
        console.warn('Failed to sync refreshed token to localStorage:', err);
      }
    } else {
      localStorage.removeItem('bearer_token');
    }
  });
}

if (app) syncBearerToken();

/**
 * Get a fresh auth token — calls Firebase getIdToken() which returns cached token
 * if still valid, or auto-refreshes if expired. Falls back to localStorage for
 * contexts where Firebase auth may not be initialized (e.g. worker iframes).
 */
export const getAuthToken = async (): Promise<string | null> => {
  try {
    const token = await getCurrentUserToken();
    if (token) return token;
  } catch {}
  return localStorage.getItem('bearer_token');
};

export { auth };